import { Component, effect, model, signal, OnInit, ViewChild, untracked, computed, HostListener } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { SessionService } from '../../services/session-service';
import { ActivatedRoute, Router } from '@angular/router';
import { ImportSitesFromClientModalComponent } from './import-sites-from-client-modal-component/import-sites-from-client-modal-component';
import { MatButtonModule } from '@angular/material/button';
import { AgGridModule } from 'ag-grid-angular';
import { CellEditRequestEvent, ColDef, ColGroupDef, GetRowIdParams, GridApi, GridReadyEvent, RowClassRules, RowSelectionOptions, SelectionChangedEvent, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { EligibilitySite } from '../../models/eligibility-site';
import { EligibilitySession } from '../../models/eligibility-session';
import { MatTooltip } from "@angular/material/tooltip";
import { MatIconModule } from '@angular/material/icon';
import { EditSessionNameDialogComponent } from './edit-session-name-dialog-component/edit-session-name-dialog-component';
import { ConfirmationDialogComponent } from '../util/confirmation-dialog-component/confirmation-dialog-component';
import { ImportSitesFromCsvModalComponent } from './import-sites-from-csv-modal-component/import-sites-from-csv-modal-component';
import { LoadingOverlayComponent } from '../util/loading-overlay-component/loading-overlay-component';
import { IconCellRendererComponent } from '../util/icon-cell-renderer-component/icon-cell-renderer-component';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from "@angular/material/select";
import { FormsModule } from '@angular/forms';
import { SessionMessageService } from '../../services/session-message-service';
import { AG_GRID_LOCALE_FR } from '@ag-grid-community/locale';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { Subscription } from 'rxjs';
import { SessionMessageBody } from '../../models/session-message-body';
import { SitesProgressBarComponent } from '../sites-progress-bar-component/sites-progress-bar-component';
import { SnackBarService } from '../../services/snack-bar-service';
import { SiteInfoDialogComponent } from '../site-info-dialog-component/site-info-dialog-component';
import { TechnicalEligibilityApiService } from '../../services/technical-eligibility-api-service';
import { MapComponent } from "./map-component/map-component";
import { AngularSplitModule, SplitComponent } from 'angular-split';
import { HoverClassDirective } from '../../directives/hover-class-directive';
import { SessionSitesHeaderComponent } from "./session-sites-header-component/session-sites-header-component";
import { MatInputModule } from '@angular/material/input';
import { EXTERNAL_MAP_PROVIDERS, ExternalMapProvidersService } from '../../services/external-map-providers-service';
import { RunEligibilityModalComponent } from './run-eligibility-modal-component/run-eligibility-modal-component';
import { AccessCellRendererComponent } from '../util/access-cell-renderer-component/access-cell-renderer-component';
import { SelectOfferModalComponent } from './select-offer-modal-component/select-offer-modal-component';
import { ImportSitesFromManualInputModalComponent } from './import-sites-from-manual-input-modal-component/import-sites-from-manual-input-modal-component';
import { TechnicalEligibilitySiteDetailModalComponent } from './technical-eligibility-site-detail-modal-component/technical-eligibility-site-detail-modal-component';
import { COMMERCIAL_ELIGIBILITY_ENABLED } from '../../app';
import { ConfigurationModalComponent } from './configuration-modal-component/configuration-modal-component';
import { DatePipe } from '@angular/common';

export type DisplayMode = 'addressValidation' | 'eligibility';

@Component({
  selector: 'app-session-sites-component',
  imports: [
    MatButtonToggleModule,
    MatDialogModule,
    MatButtonModule,
    MatSnackBarModule,
    AgGridModule,
    MatInputModule,
    MatTooltip,
    MatIconModule,
    MatMenuModule,
    MatSelectModule,
    FormsModule,
    SitesProgressBarComponent,
    AngularSplitModule,
    MapComponent,
    HoverClassDirective,
    SessionSitesHeaderComponent
  ],
  providers: [DatePipe],
  templateUrl: './session-sites-component.html',
  styleUrl: './session-sites-component.css',
})
export class SessionSitesComponent implements OnInit {
  private gridApi!: GridApi;

  private readonly gridApiSignal = signal<GridApi | null>(null);
  private readonly sitesFetched = signal<boolean>(false); // If set to false, indicates that we need to fetch sites
  private readonly canFetchSites = computed<boolean>(() => {
    const gridApi = this.gridApiSignal();
    const session = this.session();

    return session !== null && gridApi !== null;
  }); // Tracks changes to session and gridApi so that sites are fetched only when both are defined

  public readonly foundSelectedSites = computed<EligibilitySite[]>(() => {
    const selectedSites = this.selectedSites();
    const consideredSites = selectedSites.length > 0 ? selectedSites : this.sites();

    return consideredSites.filter(site => site.validatedAddressState !== 'not_found');
  });

  public readonly toBeTestedForEligibilitySites = computed<EligibilitySite[]>(() => {
    const foundSelectedSites = this.foundSelectedSites();

    return foundSelectedSites.filter(site => site.latestSiteRequest?.state !== 'done');
  });

  private nova2GridApi!: GridApi;

  public session = signal<EligibilitySession | null>(null);
  public sites = signal<EligibilitySite[]>([]);
  public readonly sitesMap = computed<any>(() => {
    const sites = this.sites();
    
    return Object.fromEntries(sites.map(site => [site.id, site]));
  });
  private sessionCode!: string;

  public readonly gridContext = {
    componentParent: this
  };
  public readonly EMPTY_SITE: any = { 
    id: null,
    name: null,
    reference: null,
    sourceAddress: {}, 
    validatedAddress: {},
    isAddRow: true 
  }

  public readonly ADDRESS_STATES = [
    { label: 'Fiabilisé', value: 'confirmed', icon: 'where_to_vote', class: 'success-icon' },
    { label: 'Incertain', value: 'unsure', icon: 'not_listed_location', class: 'warning-icon' },
    { label: 'Introuvable', value: 'not_found', icon: 'wrong_location', class: 'error-icon' },
  ];

  pinnedBottomRowData = signal<[any]>([JSON.parse(JSON.stringify(this.EMPTY_SITE))]);
  
  public displayMode = signal<DisplayMode>('addressValidation');

  public readonly loadingOverlayComponent: any = LoadingOverlayComponent;
  public readonly loadingOverlayComponentParams: any = { loadingMessage: 'Chargement des résultats...' };

  public readonly noRowsTemplate = `
    <div>
      <span>Aucune ligne à afficher.</span><br><br>
      <span>Veuillez ajouter des sites à la session via import ou saisie manuelle</span>
    </div>
  `;

  @ViewChild('map') map!: MapComponent;

  public readonly gridLocale = AG_GRID_LOCALE_FR;

  public readonly displayMap = signal<boolean>(true);
  
  private lastMapSize = 38;
  public readonly mapSize = model<number>(38);
  
  public readonly externalMapProviders = EXTERNAL_MAP_PROVIDERS;
  
  public readonly mapSearchInput = model<string>('');
  public readonly previewedSite = computed<EligibilitySite | null>(() => {
    const previewedSiteIndex = this.previewedSiteIndex();
    return previewedSiteIndex !== null ? this.sitesMap()[previewedSiteIndex] : null;
  });
  public readonly previewedSiteIndex = signal<number | null>(null);
  public readonly selectedSites = signal<EligibilitySite[]>([]);

  public readonly rowClassRules: RowClassRules = {
    'row-error': (params) => { return params.data.validatedAddressState === 'no_result'; },
    'previewed-site-row-highlight': (params) => {
      const previewedSite = this.previewedSite();
      if (!previewedSite) return false;
      return params.data.id === previewedSite.id;
    }
  };

  public readonly eligibilityRowClassRules: RowClassRules = {
    'row-error': (params) => ['not_found', 'no_result'].includes(params.data.validatedAddressState)
  }

  public readonly eligibilityRowSelection: RowSelectionOptions = {
    mode: 'multiRow',
    isRowSelectable: (params: any) => ['confirmed', 'unsure'].includes(params.data.validatedAddressState),
    selectAll: 'filtered',
  }
  
  private siteUpdatesSubscription!: Subscription;
  private siteCreationSubscription!: Subscription;
  private siteDeletionSubscription!: Subscription;
  private wsConnectionLostSubscription!: Subscription;

  public readonly selectionColumnDef: ColDef = {
    sortable: false,
    resizable: false,
    maxWidth: 50,
    suppressHeaderMenuButton: true,
    pinned: 'left',
  };

  @ViewChild('split') split!: SplitComponent;

  /**
   * PAGE INITIALIZATION
   */

  constructor(
    private readonly sessionService: SessionService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly snackBar: SnackBarService,
    private readonly sessionMessageService: SessionMessageService,
    private readonly technicalEligibilityApiService: TechnicalEligibilityApiService,
    private readonly datePipe: DatePipe,
  ) {
    // Recalculate column definitions on displayMode change and set column size
    effect(() => {
      const previewedSite = this.previewedSite();
      if (!previewedSite) return;

      this.gridApi.redrawRows();
    });

    effect(() => {
      // Fetch sites if and only if they can and have to be fetched
      if (!this.sitesFetched() && this.canFetchSites()) {
        untracked(() => {
          // Avoid triggering this signal again when display mode changes
          const displayMode = this.displayMode();
          this.getSessionSites(true, displayMode === 'eligibility');
        });
      }
    });

    effect(() => {
      // Sites have to be fetched again if display mode changes
      const displayMode = this.displayMode();

      this.selectedSites.set([]);
      this.gridApiSignal.set(null);
      this.sitesFetched.set(false);
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.sessionService.resetCurrentSession();
      this.session.set(null);
      this.sitesFetched.set(false);
      const code = params.get('code');

      if (!code) {
        this.snackBar.error("Code de session non défini");
        this.router.navigate(['/sessions']);
        return;
      }

      this.sessionCode = code;
      this.loadSession();
    });
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridApiSignal.set(this.gridApi);
    this.updateColumnDefs();
  }
  
  onNova2GridReady(params: GridReadyEvent) {
    this.nova2GridApi = params.api;
  }

  updateColumnDefs(): void {
    this.gridApi.setGridOption('columnDefs', this.computeColumnDefs(this.displayMode()));
    this.gridApi.sizeColumnsToFit();
  }

  ngOnDestroy(): void {
    if (this.siteUpdatesSubscription) {
      this.siteUpdatesSubscription.unsubscribe();
    }

    if (this.siteCreationSubscription) {
      this.siteCreationSubscription.unsubscribe();
    }

    if (this.siteDeletionSubscription) {
      this.siteDeletionSubscription.unsubscribe();
    }

    if (this.wsConnectionLostSubscription) {
      this.wsConnectionLostSubscription.unsubscribe();
    }
  }

  loadSession(): void {
    this.sessionService.getSession(this.sessionCode).subscribe({
      next: session => {
        this.session.set(session);
        this.watchSessionUpdates();
      },
      error: () => {
        this.snackBar.error("Session inexistante.");
        this.router.navigate(['/sessions']);
      }
    });
  }

  getRowId(params: GetRowIdParams) {
    if (params.data.isAddRow) {
      return "-1";
    }
    return String(params.data.id);
  }

  watchSessionUpdates(): void {
    this.siteUpdatesSubscription = this.sessionMessageService.getSiteUpdateNotifications$.subscribe({
      next: (res: SessionMessageBody) => {
        if (typeof(res.entityId) === 'number') {
          if (this.displayMode() === 'eligibility') {
            this.retrieveSessionSiteWithLatestSiteRequest(res.entityId);
          } else {
            this.retrieveSessionSite(res.entityId);
          }
        } else if (this.displayMode() === 'eligibility' && res.attributes?.includes('siteRequests')) {
          this.getSessionSites(true, true);
        }
      }
    });

    this.siteCreationSubscription = this.sessionMessageService.getSiteCreationNotifications$.subscribe({
      next: () => {
        this.getSessionSites(true);
      }
    });

    this.siteDeletionSubscription = this.sessionMessageService.getSiteDeletionNotifications$.subscribe({
      next: (res: SessionMessageBody) => {
        if (typeof(res.entityId) === 'number') {
          const index = this.sites().findIndex((site: EligibilitySite) => site.id === res.entityId);
          this.sites.update(sites => sites.toSpliced(index, 1));
        }
      }
    });

    this.wsConnectionLostSubscription = this.sessionMessageService.connectionLost$.subscribe({
      next: (failure: boolean) => {
        if (failure === true) {
          this.snackBar.error("Connexion au serveur de mises à jour en temps réel perdue, "+
            "veuillez rafraîchir la page pour recevoir les prochaines mises à jour");
        }
      }
    });
  }

  retrieveSessionSite(siteId: number) {
    this.sessionService.getSessionSite(this.sessionCode, siteId).subscribe({
      next: response => {
        this.sites.update(sites => sites.map(site => site.id === response.id ? response : site));

        this.map.addSiteMarker(response.id, response);
      },
      error: () => {
        this.snackBar.error("Erreur rencontrée lors de la recuperation du site.");
      }
    });
  }

  retrieveSessionSiteWithLatestSiteRequest(siteId: number) {
    this.sessionService.getSessionSiteWithLatestSiteRequest(this.sessionCode, siteId).subscribe({
      next: response => {
        this.sites.update(sites => sites.map(site => site.id === response.id ? response : site));
      },
      error: () => {
        this.snackBar.error("Erreur rencontrée lors de la recuperation du site.");
      }
    });
  }

  getSessionSites(reloadIfPresent: boolean = false, includeTeResults: boolean = false) {
    this.gridApi.setGridOption('loading', true);
    this.sessionService.getSessionSites(this.sessionCode, reloadIfPresent, includeTeResults).subscribe({
      next: response => {
        this.sites.set(response.items);

        if (this.displayMode() === 'addressValidation') {
          this.map.clearSiteMarkers();
          this.map.addSiteMarkers(this.sites());
        }

        this.gridApi.setGridOption('loading', false);
        this.sitesFetched.set(true);
      },
      error: () => {
        this.snackBar.error('Erreur lors de la récupération des sites de la session');
        this.gridApi.setGridOption('loading', false);
      }
    });
  }

  /**
   * COLUMN DEFINITIONS
   */

  readonly VALIDATION_STATE_COLUMN: ColDef = {
    headerName: "État fiab.",
    sortable: true,
    filter: "agSetColumnFilter",
    floatingFilter: true,
    enableCellChangeFlash: true,
    valueGetter: (params: ValueGetterParams) => {
      switch (params.data.validatedAddressState) {
        case 'confirmed':
          return 'Fiabilisé';
        case 'not_found':
          return 'Introuvable';
        case 'unsure':
          return 'Incertain';
        case 'no_result':
          return 'Aucun resultat trouvé';
        default:
          return '-';
      }
    },
    cellRenderer: IconCellRendererComponent,
    cellRendererParams: {
      iconParams: [
        {
          icon: 'where_to_vote',
          tooltip: 'Fiabilisé',
          condition: (data: any) => data.validatedAddressState === "confirmed",
          color: 'success-icon',
          onClick: (data: any) => this.displaySiteInfo(data),
        },
        {
          icon: 'wrong_location',
          tooltip: 'Introuvable - Éligibilité technique impossible',
          condition: (data: any) => data.validatedAddressState === "not_found",
          color: 'error-icon',
          onClick: (data: any) => this.displaySiteInfo(data),
        },
        {
          icon: 'not_listed_location',
          tooltip: 'Incertain - Incohérence adresse',
          condition: (data: any) => data.validatedAddressState === "unsure" && data.validatedAddressStateReason == "address_mismatch",
          color: 'warning-icon',
          onClick: (data: any) => this.displaySiteInfo(data),
        },
        {
          icon: 'not_listed_location',
          tooltip: 'Incertain - Incohérence coord.',
          condition: (data: any) => data.validatedAddressState === "unsure" && data.validatedAddressStateReason == "coordinates_mismatch",
          color: 'warning-icon',
          onClick: (data: any) => this.displaySiteInfo(data),
        },
        {
          icon: 'not_listed_location',
          tooltip: 'Incertain - Score faible',
          condition: (data: any) => data.validatedAddressState === "unsure" && data.validatedAddressStateReason == "low_score",
          color: 'warning-icon',
          onClick: (data: any) => this.displaySiteInfo(data),
        },
        {
          icon: 'not_listed_location',
          tooltip: 'Incertain - Incohérence code immeuble',
          condition: (data: any) => data.validatedAddressState === "unsure" && data.validatedAddressStateReason == "fibre_building_code_mismatch",
          color: 'warning-icon',
          onClick: (data: any) => this.displaySiteInfo(data),
        },
        {
          icon: 'not_listed_location',
          tooltip: 'Incertain',
          condition: (data: any) => data.validatedAddressState === "unsure",
          color: 'warning-icon',
          onClick: (data: any) => this.displaySiteInfo(data),
        },
        {
          icon: 'warning',
          tooltip: 'Aucun resultat trouvé',
          condition: (data: any) => data.validatedAddressState === "no_result",
          onClick: (data: any) => this.displaySiteInfo(data),
        },
        {
          tooltip: 'Fiabilisation en cours...',
          condition: (data: any) => !data.isAddRow && !data.validatedAddressState,
          loading: (params: any) => {
            // Check whether to animate or not to save resources
            const rowIndex = params.node.rowIndex;
            const firstRow = this.gridApi.getFirstDisplayedRowIndex();
            const lastRow = this.gridApi.getLastDisplayedRowIndex();
            
            return (rowIndex >= firstRow && rowIndex <= lastRow);
          }
        },
      ]
    },
    minWidth: 85,
    maxWidth: 120
  };

  readonly SITE_INFO_COLUMNS: ColDef[] = [
    {
      headerName: "Nom site",
      field: "name",
      filter: "agTextColumnFilter",
      floatingFilter: true,
      editable: true,
      enableCellChangeFlash: true,
      pinned: 'left',
      minWidth: 175,
      columnGroupShow: 'closed',
    },
    {
      headerName: "Nom site",
      field: "name",
      filter: "agTextColumnFilter",
      floatingFilter: true,
      editable: true,
      enableCellChangeFlash: true,
      pinned: 'left',
      minWidth: 175,
      columnGroupShow: 'open',
    },
    {
      headerName: "#",
      field: "id",
      floatingFilter: false,
      editable: false,
      enableCellChangeFlash: false,
      pinned: 'left',
      minWidth: 75,
      maxWidth: 75,
      columnGroupShow: 'open',
    },
    {
      headerName: "Réf. site",
      field: "reference",
      filter: "agTextColumnFilter",
      floatingFilter: true,
      editable: true,
      enableCellChangeFlash: true,
      pinned: 'left',
      minWidth: 100,
      columnGroupShow: 'open',
    }
  ];

  computeColumnDefs(displayMode: DisplayMode): (ColDef | ColGroupDef)[] {
    let columnDefs: (ColDef | ColGroupDef)[] = [];
    switch (displayMode) {
      case 'addressValidation':
        columnDefs = [
        {
          headerName: "Infos site",
          children: this.SITE_INFO_COLUMNS
        },
        {
          headerName: "Données source",
          children: this.generateAddressColumnDef('normalized')
        },
        {
          headerName: "Données fiabilisées",
          children: this.generateAddressColumnDef('validated')
        },
        {...this.VALIDATION_STATE_COLUMN, headerName: 'État', pinned: 'right' },
        {
          headerName: "Vérif.",
          sortable: false,
          filter: false,
          enableCellChangeFlash: true,
          cellRenderer: IconCellRendererComponent,
          cellRendererParams: {
            iconParams: [
              {
                icon: 'smart_toy',
                tooltip: 'Fiabilisé automatiquement',
                condition: (data: any) => !data.dateCheckedByHuman && data.validatedAddressState && data.validatedAddressState !== "no_result",
              },
              {
                icon: 'person',
                tooltip: (rowData: any) => `Vérifié manuellement le ${this.datePipe.transform(rowData.dateCheckedByHuman, 'dd/MM/yy à HH:mm')} par ${rowData.lastCheckedBy}`,
                condition: (data: any) => data.dateCheckedByHuman,
              },
            ]
          },
          minWidth: 85,
          maxWidth: 100,
          pinned: 'right',
        },
      ]
      break;
    case 'eligibility':
      columnDefs = [
        {
          headerName: "Infos site",
          children: this.SITE_INFO_COLUMNS
        },
        {
          headerName: "Typologie de site",
          hide: !COMMERCIAL_ELIGIBILITY_ENABLED
        },
        {
          headerName: "Opérateur d'infrastructure",
          field: `validatedAddress.fibre_building_operator`,
          floatingFilter: true,
          valueFormatter: params => {
            if (!params.value) {
              return '';
            }
            
            return this.technicalEligibilityApiService.getInfrastructureOperatorByCode(params.value)?.name ?? '';
          },
          minWidth: 150
        },
        this.VALIDATION_STATE_COLUMN,
        {
          headerName: 'Etat élig.',
          field: 'latestSiteRequest.state',
          maxWidth: 150,
          valueFormatter: (params: ValueFormatterParams) => this.getTechnicalEligibilityState(params.value),
          tooltipField: 'errorMessage'
        },
        {
          headerName: 'Fibre',
          children: this.technicalEligibilityApiService.getTechnologiesByType('Fibre')
          .map((technology) => this.generateTechnologyColumnDef(technology.name))
        },
        {
          headerName: 'Accès 1',
          field: 'access1',
          cellRenderer: AccessCellRendererComponent,
          cellRendererParams: {
            editClicked: (rowData: any) => {
              this.openSelectOfferModal(rowData);
            }
          },
          hide: !COMMERCIAL_ELIGIBILITY_ENABLED,
          minWidth: 200
        },
        {
          headerName: 'Accès 2',
          field: 'access2',
          cellRenderer: AccessCellRendererComponent,
          hide: !COMMERCIAL_ELIGIBILITY_ENABLED,
          editable: true,
          minWidth: 200
        }
      ];
      break;
    }
    
    this.previewedSiteIndex.set(null);
    return columnDefs;
  }

  generateTechnologyColumnDef(technology: string): ColDef {
    return {
      headerName: this.technicalEligibilityApiService.getTechnologyDisplayedName(technology),
      // valueGetter: (params: ValueGetterParams) => this.getSimpleRowValue(params.data, technology),
      cellRenderer: IconCellRendererComponent,
      cellRendererParams: {
        iconParams: [
          {
            icon: 'check_circle',
            tooltip: 'Éligible - Cliquez pour plus de détails',
            badge: (rowData: any) => this.hasFibreBuildingCodeMismatch(rowData.latestSiteRequest.providerResponses, technology) ? "!" : null,
            badgeColorClass: 'badge-warning',
            onClick: (data: any) => this.dialog.open(TechnicalEligibilitySiteDetailModalComponent, {
              width: '80%',
              data: { siteName: data.name, technology: technology, data: this.computeOffersByProviders(data.latestSiteRequest.providerResponses, technology), eligibilityAppUuid: data.latestSiteRequest.eligibilityAppUuid }
            }),
            condition: (rowData: any) => this.computeIconCondition(rowData, technology, 'eligible'),
            color: 'success-icon'
          },
          {
            icon: 'help',
            tooltip: 'Sur devis - Cliquez pour plus de détails',
            onClick: (data: any) => this.dialog.open(TechnicalEligibilitySiteDetailModalComponent, {
              width: '80%',
              data: { siteName: data.name, technology: technology, data: this.computeOffersByProviders(data.latestSiteRequest.providerResponses, technology), eligibilityAppUuid: data.latestSiteRequest.eligibilityAppUuid }
            }),
            condition: (rowData: any) => this.computeIconCondition(rowData, technology, 'eligible_under_quotation'),
          },
          {
            icon: 'warning',
            tooltip: 'Erreur - Cliquez pour plus de détails',
            onClick: (data: any) => this.dialog.open(TechnicalEligibilitySiteDetailModalComponent, {
              width: '80%',
              data: { siteName: data.name, technology: technology, data: this.computeOffersByProviders(data.latestSiteRequest.providerResponses, technology), eligibilityAppUuid: data.latestSiteRequest.eligibilityAppUuid }
            }),
            condition: (rowData: any) => this.computeIconCondition(rowData, technology, 'error'),
            color: 'warning-icon'
          },
          {
            icon: 'cancel',
            tooltip: 'Inéligible - Cliquez pour plus de détails',
            onClick: (data: any) => this.dialog.open(TechnicalEligibilitySiteDetailModalComponent, {
              width: '80%',
              data: { siteName: data.name, technology: technology, data: this.computeOffersByProviders(data.latestSiteRequest.providerResponses, technology), eligibilityAppUuid: data.latestSiteRequest.eligibilityAppUuid }
            }),
            condition: (rowData: any) => this.computeIconCondition(rowData, technology, 'not_eligible'),
            color: 'error-icon'
          },
          {
            loading: () => true,
            tooltip: 'Éligibilité technique en cours - Veuillez patienter...',
            condition: (rowData: any) => rowData.latestSiteRequest && (rowData.latestSiteRequest.state === 'to_process' || rowData.latestSiteRequest.state === 'in_progress'),
          }
        ]
      },
      minWidth: 70,
      enableCellChangeFlash: true
    }
  }

  generateAddressColumnDef(addressType: ('source' | 'normalized' | 'validated')): ColDef[] {
    return [
      {
        headerName: "Adresse",
        field: `${addressType}Address.address`,
        filter: "agTextColumnFilter",
        floatingFilter: true,
        columnGroupShow: 'closed',
        editable: true,
        enableCellChangeFlash: true,
        minWidth: 300,
      },
      {
        headerName: "Adresse",
        field: `${addressType}Address.address_line_1`,
        filter: "agTextColumnFilter",
        floatingFilter: true,
        columnGroupShow: 'open',
        editable: true,
        enableCellChangeFlash: true,
        minWidth: 250,
      },
      {
        headerName: "Code postal",
        field: `${addressType}Address.address_zip_code`,
        filter: "agTextColumnFilter",
        floatingFilter: true,
        columnGroupShow: 'open',
        editable: true,
        enableCellChangeFlash: true,
        minWidth: 100,
        maxWidth: 100
      },
      {
        headerName: "Commune",
        field: `${addressType}Address.address_city`,
        filter: "agTextColumnFilter",
        floatingFilter: true,
        columnGroupShow: 'open',
        editable: true,
        enableCellChangeFlash: true,
        minWidth: 150
      },
      {
        headerName: "Coordonnées (WGS84)",
        field: `${addressType}Address.coordinates`,
        editable: true,
        columnGroupShow: 'open',
        enableCellChangeFlash: true,
      },
      {
        headerName: "Code Immeuble",
        field: `${addressType}Address.fibre_building_code`,
        editable: true,
        enableCellChangeFlash: true,
        columnGroupShow: 'open',
      },
    ]
  }

  /**
   * MAP METHODS
   */

  ngAfterViewInit(): void {
    this.split.dragProgress$.subscribe((event) => {
      this.mapSize.set(event.sizes[1] as number);
      this.lastMapSize = this.mapSize();
    });
  }

  toggleMap() {
    if (this.mapSize() > 0) {
      this.lastMapSize = this.mapSize();
      this.mapSize.set(0);
    } else {
      this.mapSize.set(this.lastMapSize);
    }
  }

  onSplitDragEnd(event: any) {
    this.lastMapSize = event.sizes[1] as number;
    this.mapSize.set(this.lastMapSize);

    if (!this.gridApi) return;

    this.gridApi.sizeColumnsToFit();
    this.gridApi.redrawRows();
  }

  selectSite(site: EligibilitySite) {
    if (!site.validatedAddressState) return;

    const currentSiteId = this.previewedSite()?.id;

    this.map.zoomToSiteMarker(site.id);
    this.map.getSiteFibreBuildings(site);

    if (currentSiteId === site.id) return;
    
    // TODO: Get a reliable rowNodeIndex instead of computing on the fly
    const rowNodeIndex = site.id - this.sites()[0].id;
    this.gridApi.ensureIndexVisible(rowNodeIndex, 'middle');
    this.previewedSiteIndex.set(site.id);
  }

  unselectSites(): void {
    this.previewedSiteIndex.set(null);
    this.gridApi.redrawRows();

    this.map.clearFibreBuildingSiteMarkers();
    this.map.zoomToSiteMarkers();
  }

  selectSiteById(siteId: number) {
    const site = this.sitesMap()[siteId];
    if (site) {
      this.selectSite(site);
    } else {
      this.unselectSites();
    }
  }  

  computeIconCondition(rowData: any, technology: string, state: string): boolean {
    const providerResponses = rowData.latestSiteRequest?.providerResponses || [];
    for (const providerResponse of providerResponses) {
      if (state === 'error') {
        if (providerResponse['status'] === state) {
          return true;
        }
      } else {
        for (const technologyResponse of providerResponse.technologyResponses) {
          if (technology === technologyResponse.technology && technologyResponse.feasibility === state) {
            return true;
          }
        }
      }
    }
    return false;
  }

  hasFibreBuildingCodeMismatch(providerResponses: any[], technology: string): boolean {
    // FTTH is the only technology that requires fibre building codes for ordering
    if (technology !== "ftth") {
      return false;
    }

    return providerResponses.some((providerResponse) => providerResponse.fibreBuildingCodesMismatch);
  }

  computeOffersByProviders(providerResponses: any[], technology: string): any {
    let technicalEligibilityResult: any = {};

    for (const providerResponse of providerResponses) {
      if (providerResponse.state == "error" || providerResponse.state == "failed") {
        technicalEligibilityResult[providerResponse.provider] = {
          technology: technology,
          feasibility: 'error',
          maxDownloadRate: null,
          maxUploadRate: null,
          pricingZones: null,
          commercialRangeResponses: null,
          eligibilityMode: null,
          executionTime: null,
          technicalInformation: null,
          fibreBuildingCodes: null,
          fibreBuildingCodesMismatch: null
        }

        continue
      }

      const foundResponse = providerResponse.technologyResponses.find((technologyRes: any) => technologyRes.technology === technology);

      technicalEligibilityResult[providerResponse.provider] = {
        ...foundResponse,
        eligibilityMode: providerResponse.eligibilityMode,
        executionTime: providerResponse.executionTime,
        technicalInformation: providerResponse.technicalInformation,
        fibreBuildingCodes: providerResponse.fibreBuildingCodes,
        fibreBuildingCodesMismatch: providerResponse.fibreBuildingCodesMismatch
      }
    }

    return technicalEligibilityResult;
  }

  getTechnicalEligibilityState(state?: string): string {
    if (!state) {
      return '';
    }

    switch(state) {
      case 'to_process':
        return 'En attente de traitement';
      case 'in_progress':
        return 'En cours de traitement';
      case 'done':
        return 'Terminé';
      case 'failed':
        return 'Terminé avec erreur(s)';
      case 'unprocessed':
        return 'En cours de traitement';
      default:
        return '';
    }
  }

  getExternalMapProviderUrl(provider: string) {
    const previewedSite = this.previewedSite();

    if (!previewedSite?.validatedAddress) return;

    const latitude = previewedSite.validatedAddress.coordinates_latitude;
    const longitude = previewedSite.validatedAddress.coordinates_longitude;

    if (!latitude || !longitude) {
      return;
    }

    return ExternalMapProvidersService.getExternalMapProviderUrl(provider, latitude, longitude);
  }

  moveSiteToCoordinates() {
    // Avoid regex ReDoS vulnerability
    const pattern = /-?\d{1,3}\.\d{1,7}/g;
    
    const matches = this.mapSearchInput().match(pattern);

    if (matches && matches.length === 2) {
      const previewedSite = this.previewedSite();
      if (previewedSite !== null) {
        this.map.moveSiteMarker(previewedSite.id, +matches[0], +matches[1]);
      }
    } else {
      this.snackBar.error('Coordonnées invalides !');
    }
  }

  onMoveSiteMarker(event: any) {
    const siteId = event.siteIndex;
    const site = this.sitesMap()[siteId];

    if (!site?.validatedAddress) {
      // Site not found
      return;
    }

    site.validatedAddress.coordinates_latitude = Number(event.latitude);
    site.validatedAddress.coordinates_longitude = Number(event.longitude);
    site.validatedAddress.coordinates = `${site.validatedAddress.coordinates_latitude}, ${site.validatedAddress.coordinates_longitude}`;

    if (site.validatedAddress.fibre_building_code && !event.bypassConfirmation) {
      // The site has a fibre building code : confirm with user if they want to keep it or not

      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {content: "Ce site est actuellement associé à un code immeuble - voulez-vous le conserver ?"},
      });
  
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          // The user wants to keep the fibre building code, exit
          this.selectSite(site);
          return;
        }

        // TODO: Finish this
        const newSite = this.resetSiteAddressAndFibreBuilding(site);
        this.updateSessionSite(siteId, newSite, true, false, false);
      });
    } else {
      const newSite = this.resetSiteAddressAndFibreBuilding(site);
      this.updateSessionSite(siteId, newSite, true, false, false);
    }
  }

  resetSiteAddressAndFibreBuilding(site: any): any {
    site.validatedAddress.address = null;
    site.validatedAddress.address_street_number = null;
    site.validatedAddress.address_street_name = null;
    site.validatedAddress.address_street_type = null;
    site.validatedAddress.address_street_number_complement = null;
    site.validatedAddress.address_street_code = null;
    site.validatedAddress.address_line_1 = null;
    site.validatedAddress.address_line_2 = null;
    site.validatedAddress.address_zip_code = null;
    site.validatedAddress.address_city_code = null;
    site.validatedAddress.address_city = null;
    site.validatedAddress.address_country = null;
    site.validatedAddress.fibre_building_code = null;
    site.validatedAddress.fibre_building_state = null;
    site.validatedAddress.fibre_building_operator = null;
    site.validatedAddressState = "not_found";
    return site;
  }

  onClickFibreBuildingSiteMarker(fibreBuildingSiteIndex: number) {
    const currentSite = this.previewedSite();

    if (!currentSite) return;

    const fibreBuildingSite = this.map.getFibreBuildingSiteByIndex(fibreBuildingSiteIndex);

    if (!fibreBuildingSite) return;

    const newSite: EligibilitySite = {
      ...currentSite, 
      validatedAddress: fibreBuildingSite.sourceAddress, 
      validatedAddressState: 'confirmed'
    };
    
    if (!newSite.validatedAddress?.coordinates_latitude 
      || !newSite.validatedAddress?.coordinates_longitude) {
        return;
    }
    
    this.updateSessionSite(currentSite.id, newSite);

    this.selectSite(newSite);
  }

  onCurrentSiteAddressStateChange(newAddressState: string): void {
    const currentSite = this.previewedSite();

    if (!currentSite || !['confirmed', 'not_found', 'unsure'].includes(newAddressState)) return;

    const newSite: EligibilitySite = {
      ...currentSite,
      validatedAddressState: newAddressState
    }

    this.updateSessionSite(currentSite.id, newSite);
    this.selectSiteById(currentSite.id + 1);
  }

  /* Used to listen for left / right arrow keys to move to next / previous site and escape key for site deselection */
  @HostListener('window:keyup', ['$event'])
  onKeyUp(event: KeyboardEvent) {
    if (this.displayMode() === 'eligibility') return;

    // Prevent keys to interfere with mat-select or input fields
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || 
        target.tagName === 'SELECT' || 
        target.tagName === 'TEXTAREA' ||
        target.closest('mat-select')) {
      return;
    }

    event.stopPropagation();

    const previewedSiteId = this.previewedSite()?.id;

    if (!previewedSiteId) return;

    // TODO: use the filtered list instead of going id + 1

    switch(event.key) {
        case 'ArrowRight':
          this.selectSiteById(previewedSiteId + 1);
          break;
        case 'ArrowLeft':
          this.selectSiteById(previewedSiteId - 1);
          break;
        case 'Escape':
          this.unselectSites();
          break;
      }
  }

  /**
   * USER ACTIONS
   */

  openSelectOfferModal(site: EligibilitySite) {
    const dialogRef = this.dialog.open(SelectOfferModalComponent, {
      width: '85%',
      height: '60%',
      data: {
        site: site
      }
    });
  }

  openEligibilityModal() {
    const dialogRef = this.dialog.open(RunEligibilityModalComponent, {
      width: '60%',
      data: {
        sessionCode: this.sessionCode,
        siteIds: this.foundSelectedSites()
          .map(site => site.id),
        eligibleSiteIds: this.toBeTestedForEligibilitySites()
          .map(site => site.id)
      }
    });

    dialogRef.afterClosed().subscribe((requestDone: boolean) => {
      if (requestDone) {
        this.gridApi.setGridOption('loading', true);
      }
    });
  }

  openAccessConfigurationModal(): void {
    const dialogRef = this.dialog.open(ConfigurationModalComponent, {
      width: '80%',
      height: '40%',
      minHeight: '500px'
    });
  }

  editSessionName() {
    const dialogRef = this.dialog.open(EditSessionNameDialogComponent, {
      data: {
        session: this.session()
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result) {
        return;
      }

      this.session.set(result);
    });
  }

  onSiteSelectionChanged(event: SelectionChangedEvent): void {
    this.selectedSites.set(event.api.getSelectedRows());
  }

  deleteSelectedSites(): void {
    if (!this.sessionCode) {
      return;
    }

    if (this.selectedSites().length == 0) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {content: `Êtes-vous sûr de vouloir supprimer ces ${this.selectedSites().length} sites ?`},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      const selectedSiteIds = this.selectedSites().map(selectedSite => selectedSite.id);

      this.sessionService.deleteSessionSites(this.sessionCode, selectedSiteIds).subscribe({
        next: () => {
          this.snackBar.success("Sites supprimés");
          
          this.sites.update(sites => sites.filter(site => !selectedSiteIds.includes(site.id)));

          selectedSiteIds.forEach((siteId: number) => this.map.removeSiteMarker(siteId));
        },
        error: () => {
          this.snackBar.error("Erreur rencontrée lors de la suppression des sites.");
        }
      });
    });
  }

  launchSelectedSitesFiab(): void {
    if (!this.sessionCode) {
      return;
    }

    if (this.selectedSites().length == 0) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {content: `Êtes-vous sûr de vouloir relancer la fiabilisation pour ces ${this.selectedSites().length} sites ?`},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      const selectedSiteIds = this.selectedSites().map(selectedSite => selectedSite.id);

      this.sessionService.launchSessionSitesFiab(this.sessionCode, selectedSiteIds).subscribe({
        next: () => {
          this.snackBar.success("Fiabilisation lancée");
          this.sites.update(sites => sites.map(site => selectedSiteIds.includes(site.id)
            ? {
                ...site,
                matchedAddress: undefined,
                matchedAddressMethod: undefined,
                matchedAddressScore: undefined,
                validatedAddress: undefined,
                validatedAddressState: undefined,
                lastCheckedBy: undefined,
                dateCheckedByHuman: undefined,
              }
            : site
          ));
        },
        error: () => {
          this.snackBar.error("Erreur rencontrée");
        }
      });
    });
  }

  launchSiteFiab(site: EligibilitySite): void {
    if (!this.sessionCode) {
      return;
    }

    this.sessionService.launchSessionSiteFiab(this.sessionCode, site.id).subscribe({
      next: (response: EligibilitySite) => {
        this.sites.update(sites => sites.map(site => site.id === response.id ? response : site));
        this.sessionService.updateCachedSessionSite(response);
        this.snackBar.success("Fiabilisation lancée");
      },
      error: () => {
        this.snackBar.error("Erreur rencontrée");
      }
    });
  }

  deleteSite(site: any, index: number): void {
    if (!this.sessionCode) {
      return;
    }

    const sessionCode = this.sessionCode;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {content: "Êtes-vous sûr de vouloir supprimer ce site ?"},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.sessionService.deleteSessionSite(sessionCode, site.id).subscribe({
        next: () => {
          // Remove site from sites list
          this.sites.update(sites => sites.filter(currentSite => currentSite.id != site.id));
          
          // Inform user
          this.snackBar.success("Site supprimé.");
        },
        error: () => {
          this.snackBar.error("Erreur rencontrée lors de la suppression du site.");
        }
      });
    });
  }

  onCellEditRequest(event: CellEditRequestEvent) {
    let field = event.colDef.field
    if (event.colDef.field === undefined || field === undefined) {
      return;
    }

    let parentField: string, childField: string = '';
    let data: any;

    if (field.indexOf(".") >= 0) {
      // Field name contains a "." so it's something like "sourceAddress.address_line_1"
      childField = field.split(".")[1];
      parentField = field.split(".")[0];

      data = event.data[parentField];

      if (!data) {
        data = {};
      }

      data[childField] = event.newValue ? event.newValue : "";

      Object.keys(data).forEach((field) => {
        if (field !== childField) {
          delete data[field];
        }
      });
    } else {
      // Field name does not contain a "." so it's something like "name"

      parentField = event.colDef.field;
      data = event.newValue ? event.newValue : "";
    }
    
    // Push update to server
    if (!event.data.isAddRow) {
      const updatedSite: any = {};
      updatedSite[parentField] = data;
      this.updateSessionSite(event.data.id, updatedSite, false, parentField === 'normalizedAddress');
    } else {
      event.data[parentField] = data;
      event.api.redrawRows({rowNodes: [event.node]});
    }
  }

  updateSessionSite(siteId: number, newSite: any, selectSiteAfterwards: boolean = false, relaunchFiabAfterwards: boolean = false, excludeNone: boolean = true) {
    this.sessionService.updateSessionSite(this.sessionCode, siteId, newSite, excludeNone).subscribe({
      next: (response) => {
        this.sites.update(sites => sites.map(site => site.id === response.id ? response : site));

        if (selectSiteAfterwards) {
          this.selectSite(response);
        }

        if (relaunchFiabAfterwards) {
          const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {content: `Les données source de ce site ont été modifiées. Voulez-vous relancer la fiabilisation pour ce site ?`},
          });

          dialogRef.afterClosed().subscribe(result => {
            if (!result) {
              return;
            }
            
            this.launchSiteFiab(response);
          });
        }
      }, 
      error: (err) => {
        this.snackBar.error("Erreur rencontrée lors de la mise à jour du site");
      },
    });
  }

  duplicateSession() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {content: "Êtes-vous sûr de vouloir dupliquer cette session ?"},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.sessionService.duplicateSession(this.sessionCode).subscribe({
        next: (session) => {
          this.router.navigate(['sessions', session.code]);
          this.snackBar.success("Session dupliquée.");
        },
        error: () => {
          this.snackBar.error("Erreur lors de la duplication de session");
        }
      });
    });
  }

  importFromManualInput() {
    const dialogRef = this.dialog.open(ImportSitesFromManualInputModalComponent, {
      width: '80%',
      data: {
        sessionCode: this.sessionCode
      }
    });

    dialogRef.afterClosed().subscribe((site) => {
      this.updateSitesPostImport([site]);
    });
  }

  importFromExistingClient() {
    const dialogRef = this.dialog.open(ImportSitesFromClientModalComponent, {
      width: '60%',
      height: '80%',
      data: {
        sessionCode: this.sessionCode
      }
    });

    dialogRef.afterClosed().subscribe((sites) => {
      this.updateSitesPostImport(sites);
    });
  }

  importFromCSV() {
    const dialogRef = this.dialog.open(ImportSitesFromCsvModalComponent, {
      width: '80%',
      height: '80%',
      data: {
        sessionCode: this.sessionCode
      }
    });

    dialogRef.afterClosed().subscribe((sites) => {
      this.updateSitesPostImport(sites);
    });
  }

  updateSitesPostImport(sites: any) {
    if (sites) {
      this.sites.update((currentSites: EligibilitySite[]) => {
        return [...currentSites, ...sites];
      });

      this.sessionService.updateCurrentSessionSites(this.sites());
    }
  }
  
  exportAsStdXLSX() {
    this.sessionService.exportSessionSites(this.sessionCode, this.selectedSites().map(site => site.id)).subscribe({
      next: (response: any) => {
        let dataType = response.type;
        let binaryData = [];
        binaryData.push(response);
        let downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, {type: dataType}));
        document.body.appendChild(downloadLink);
        downloadLink.click();
      }
    });
  }

  notifyUrlCopied() {
    this.snackBar.success("Lien de session copié dans le presse-papiers !");
  }

  displaySiteInfo(site: EligibilitySite) {
    this.dialog.open(SiteInfoDialogComponent, {
      width: '80%',
      data: {
        site: site
      }
    });
  }

  // Nova 2 invisible grid to generate Excel exports

  nova2columns: ColDef[] = [
    {
      headerName: 'Mode éligibilité', 
      valueGetter: (params: ValueGetterParams) => this.getRowNova2EligibilityMode(params.data)
    },
    {
      headerName: 'Référence site', field: 'name'
    },
    {
      headerName: 'Nom Client / Enseigne'
    },
    {
      headerName: 'Adresse complète', 
      valueGetter: (params: ValueGetterParams) => this.getRowNova2Address(params.data)
    },
    {
      headerName: 'GPS Lat/Long', 
      valueGetter: (params: ValueGetterParams) => this.getRowNova2Coordinates(params.data)
    },
    {
      headerName: 'NDI'
    },
    {
      headerName: 'Code Immeuble', 
      field: 'validatedAddress.fibre_building_code'
    },
    {
      headerName: 'Type de site'
    },
    {
      field: 'state',
      headerName: 'Etat fiabilisation',
      editable: false,
      valueGetter: (params: ValueGetterParams) => this.getRowNova2AddressState(params.data)
    }
  ];

  getRowNova2EligibilityMode(row: any) {
    if (row.validatedAddress?.fibre_building_code) {
      return 'IPE';
    }

    if (row.validatedAddress?.coordinates_latitude && row.validatedAddress?.coordinates_longitude) {
      return 'GPS';
    }

    return 'adresse';
  }

  getRowNova2Address(row: any) {
    if (!row) {
      return;
    }

    const addressParts = [];

    if (row.validatedAddress.address_line_1) {
      addressParts.push(row.validatedAddress.address_line_1);
    }

    if (row.validatedAddress.address_line_2) {
      addressParts.push(row.validatedAddress.address_line_2);
    }

    if (row.validatedAddress.address_zip_code) {
      addressParts.push(row.validatedAddress.address_zip_code);
    }

    if (row.validatedAddress.address_city) {
      addressParts.push(row.validatedAddress.address_city);
    }

    if (addressParts.length > 0) {
      return addressParts.join(", ");
    }

    return;
  }

  getRowNova2AddressState(row: any) {
    switch (row.validatedAddressState) {
      case 'confirmed':
        return 'Fiabilisé';
      case 'unsure':
        return 'Incertain';
      case 'not_found':
        return 'Introuvable';
      case 'no_result':
        return 'Aucun resultat';
      default:
        return '-';
    }
  }

  getRowNova2Coordinates(row: any) {
    if (!row) {
      return;
    }

    if (!row.validatedAddress.coordinates_latitude || !row.validatedAddress.coordinates_longitude) {
      return;
    }

    return `${row.validatedAddress.coordinates_latitude}, ${row.validatedAddress.coordinates_longitude}`;
  }

  exportAsNova2CSV() {
    this.nova2GridApi.exportDataAsCsv();
  }

  exportAsNova2XLSX() {
    this.nova2GridApi.exportDataAsExcel();
  }
}
